import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, Zap, XCircle, Archive, Trash2, RotateCcw, Users, CheckSquare, Loader2, ShieldCheck, User as UserIcon } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/app/AppShell";

const looksLikeTest = (p) => {
  const s = `${p.name} ${p.email} ${p.agency || ""}`.toLowerCase();
  return s.includes("test") || s.includes("example.com") || s.includes("@test.");
};

export default function ManagePartners() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setPartners(await api(`/users/partners?include_archived=true`)); }
    catch (e) { alert(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "agent")) { navigate("/profile", { replace: true }); return; }
    load();
  }, [user, navigate, load]);

  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllTests = () => setSelected(new Set(partners.filter(looksLikeTest).map((p) => p.id)));
  const clearSelection = () => setSelected(new Set());
  const exitSelect = () => { setSelectMode(false); clearSelection(); };

  const bulkArchive = async () => {
    if (!window.confirm(`Archive ${selected.size} partner(s)? They will be hidden from the leaderboard but kept in records.`)) return;
    setBusy(true);
    try {
      for (const id of selected) {
        const p = partners.find((x) => x.id === id);
        if (p && !p.archived) await api(`/users/${id}/archive`, { method: "PATCH" });
      }
      exitSelect(); await load();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  const bulkDelete = async () => {
    const totalRefs = partners.filter((p) => selected.has(p.id)).reduce((s, p) => s + p.referral_count, 0);
    const msg = totalRefs > 0 ? `Permanently delete ${selected.size} partner(s) AND their ${totalRefs} referral(s)? This cannot be undone.` : `Permanently delete ${selected.size} partner(s)? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      for (const id of selected) {
        const p = partners.find((x) => x.id === id);
        const cascade = p && p.referral_count > 0;
        await api(`/users/${id}?cascade=${cascade}`, { method: "DELETE" });
      }
      exitSelect(); await load();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const archiveOne = async (p) => { try { await api(`/users/${p.id}/archive`, { method: "PATCH" }); await load(); } catch (e) { alert(e.message); } };
  const deleteOne = async (p) => {
    const hasRefs = p.referral_count > 0;
    const msg = hasRefs ? `Permanently delete ${p.name} AND their ${p.referral_count} referral(s)? This cannot be undone.` : `Permanently delete ${p.name}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try { await api(`/users/${p.id}?cascade=${hasRefs}`, { method: "DELETE" }); await load(); }
    catch (e) { alert(e.message); }
  };

  const testCount = partners.filter(looksLikeTest).length;

  return (
    <AppShell>
      <div className="px-5 lg:px-10 py-8 max-w-4xl">
        <div className="flex items-center justify-between">
          <button data-testid="back-btn" onClick={() => navigate(-1)} className="w-10 h-10 grid place-items-center -ml-2 rounded-lg hover:bg-[#F5F5F5]"><ArrowLeft size={20} /></button>
          <button data-testid="toggle-select-btn" onClick={() => selectMode ? exitSelect() : setSelectMode(true)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border-2 text-xs font-extrabold ${selectMode ? "text-white" : ""}`}
            style={{ borderColor: "#D62828", background: selectMode ? "#D62828" : "transparent", color: selectMode ? "#fff" : "#D62828" }}>
            {selectMode ? <X size={14} /> : <CheckSquare size={14} />} {selectMode ? "Cancel" : "Select"}
          </button>
        </div>

        <span className="eyebrow mt-4 block">ADMIN · MANAGE</span>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">Referral Partners</h1>
        <p className="text-sm mt-1" style={{ color: "#5A5A5A" }}>{selectMode ? `${selected.size} selected` : `${partners.length} partner${partners.length === 1 ? "" : "s"} total`}</p>

        {selectMode && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {testCount > 0 && (
              <button onClick={selectAllTests} className="chip chip-outline"><Zap size={12} /> Select all test ({testCount})</button>
            )}
            {selected.size > 0 && <button onClick={clearSelection} className="chip"><XCircle size={12} /> Clear</button>}
          </div>
        )}

        {loading ? (
          <div className="grid place-items-center py-24"><Loader2 className="animate-spin" color="#D62828" /></div>
        ) : partners.length === 0 ? (
          <div className="text-center py-16">
            <Users size={36} color="#8A8A8A" className="mx-auto" />
            <p className="mt-3" style={{ color: "#8A8A8A" }}>No partners yet.</p>
          </div>
        ) : (
          <div className="mt-5 space-y-3 pb-32">
            {partners.map((p) => {
              const isTest = looksLikeTest(p);
              const isSel = selected.has(p.id);
              return (
                <div key={p.id} data-testid={`partner-row-${p.id}`} onClick={() => selectMode && toggleSelect(p.id)}
                  className={`bg-white rounded-2xl p-5 border ${selectMode ? "cursor-pointer" : ""} ${p.archived ? "opacity-70 border-dashed" : ""}`}
                  style={{ borderColor: isSel ? "#D62828" : "#E8E8E8", borderWidth: isSel ? 2 : 1 }}>
                  <div className="flex items-start gap-3">
                    {selectMode && (
                      <div className="w-6 h-6 rounded-md border-2 grid place-items-center mt-0.5 shrink-0" style={{ background: isSel ? "#D62828" : "transparent", borderColor: isSel ? "#D62828" : "#E8E8E8" }}>
                        {isSel && <Check size={14} color="#fff" />}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-extrabold">{p.name}</span>
                        {p.archived && <span className="px-1.5 py-0.5 rounded text-[9px] font-black tracking-wider" style={{ background: "#EEE", color: "#666" }}>ARCHIVED</span>}
                        {isTest && !p.archived && <span className="px-1.5 py-0.5 rounded text-[9px] font-black tracking-wider" style={{ background: "#FEF3C7", color: "#92400E" }}>TEST</span>}
                      </div>
                      <div className="text-xs mt-1" style={{ color: "#5A5A5A" }}>{p.email}</div>
                      {p.agency && <div className="text-[11px] italic" style={{ color: "#8A8A8A" }}>{p.agency}</div>}
                      {p.parent_agent_name ? (
                        <div className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded text-[10px]" style={{ background: "#E3F2FD", color: "#1F4E79" }}>
                          <UserIcon size={10} /> under {p.parent_agent_name}
                        </div>
                      ) : user?.role === "admin" ? (
                        <div className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded text-[10px]" style={{ background: "#E3F0FB", color: "#D62828" }}>
                          <ShieldCheck size={10} /> direct partner
                        </div>
                      ) : null}
                    </div>
                    <div className="text-center px-2 py-1 rounded-xl min-w-[70px]" style={{ background: "#E3F0FB" }}>
                      <div className="font-display text-xl font-black tnum" style={{ color: "#D62828" }}>{p.referral_count}</div>
                      <div className="text-[10px] font-bold" style={{ color: "#5A5A5A" }}>{p.referral_count === 1 ? "referral" : "referrals"}</div>
                    </div>
                  </div>
                  {!selectMode && (
                    <div className="flex gap-2 mt-3 pt-3 border-t" style={{ borderColor: "#E8E8E8" }}>
                      <button data-testid={`archive-partner-${p.id}`} onClick={() => archiveOne(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full border-2 text-xs font-extrabold" style={{ borderColor: "#999", background: "#F5F5F5", color: "#666" }}>
                        {p.archived ? <RotateCcw size={12} /> : <Archive size={12} />} {p.archived ? "Restore" : "Archive"}
                      </button>
                      <button data-testid={`delete-partner-${p.id}`} onClick={() => deleteOne(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-full border-2 text-xs font-extrabold status-declined" style={{ borderColor: "#C62828" }}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {selectMode && selected.size > 0 && (
          <div className="fixed bottom-0 left-0 right-0 lg:left-72 px-5 py-4 bg-white border-t flex gap-3" style={{ borderColor: "#E8E8E8" }}>
            <button data-testid="bulk-archive-btn" onClick={bulkArchive} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-full font-extrabold border" style={{ borderColor: "#999", background: "#F5F5F5", color: "#666" }}>
              <Archive size={16} /> Archive ({selected.size})
            </button>
            <button data-testid="bulk-delete-btn" onClick={bulkDelete} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-full font-extrabold text-white" style={{ background: "#C62828" }}>
              {busy ? <Loader2 className="animate-spin" size={16} /> : (<><Trash2 size={16} /> Delete ({selected.size})</>)}
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
}
